'use client';

import { ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { cn } from '@/app/utils';
import { DEFAULT_MEDIA_ROOT } from '../../_utils';
import { AnchorCon } from '../Conversation/Anchors/AnchorCon';
import { SubtaskPanel } from '../Conversation/Messages/SubtaskPanel';
import { ChatEmptySession } from '../Conversation/States/ChatEmptySession';
import { ChatLoading } from '../Conversation/States/ChatLoading';
import { StoryDetailPanel } from '../StoryDetail';
import { ChatHeader } from './ChatHeader';
import { ChatInput, ChatInputDisclaimer } from './ChatInput';
import { MediaBrowser } from './MediaBrowser/index';
import { MessageList } from './MessageList';
import { TaskPanel } from './TaskPanel';
import type { ChatSceneCtx } from './use-chat-scene';

interface ConversationChatPanelProps {
  ctx: ChatSceneCtx;
}

/**
 * 正常对话态场景容器：`/claw/<sessionsId>` 进入时渲染。
 *
 * 承载：history loading / empty / message list、TaskPanel / SubtaskPanel、
 * AnchorCon、StoryDetailPanel、MediaBrowser、ChatInput 与 stream 禁用态联动。
 *
 * 欢迎页 hero / quick case / 申请开通流程留给 WelcomeChatPanel；
 * 共享的 state / handlers 由 useChatScene 统一管理后通过 ctx 注入。
 */
export const ConversationChatPanel = ({ ctx }: ConversationChatPanelProps) => {
  const t = useTranslations();
  const {
    chatContainerRef,
    chatScrollRef,
    messageListRef,
    chatInputRef,
    isDraggingOver,
    isShowFile,
    storyDialogMessage,
    storyDialogMessageId,
    setStoryDialogMessageId,
    pendingPreviewFilePath,
    setPendingPreviewFilePath,
    restoringSessionMedia,
    bumpClawFileListRefreshKey,
    isHistoryBootstrapping,
    historyLoading,
    isAwaitingFirstMessageAfterCreate,
    turnPhase,
    messages,
    phases,
    activeClaw,
    activeSessionId,
    sessionMediaFolderMap,
    submittingStoryMessageIds,
    hasActiveTask,
    expandedPanel,
    setExpandedPanel,
    isReady,
    chatInputSending,
    chatInputOnSend,
    onApplyForAccess,
    disableChatInputSend,
    disableChatInputUpload,
    chatInputYoloActive,
    showChatInput,
    anchors,
    handleChatScroll,
    handlePreviewFile,
    handleQuoteFile,
    handleAnchorJump,
    handleAssistantMeasureGrow,
    handleAssistantTypingEnd,
    handleOpenStoryDetail,
    handleSubmitStory,
    handleSubmitStoryFromDialog,
    isShareSelecting,
    selectedShareMessageIds,
    selectableShareMessageIds,
    isAllShareMessagesSelected,
    selectedShareMessageCount,
    sharingSession,
    syncingShareIds,
    handleToggleShareSelection,
    handleToggleShareMessage,
    handleToggleAllShareMessages,
    handleConfirmShareSelection,
    handleCancelShareSelection,
  } = ctx;

  return (
    <div className="flex h-full min-h-0 p-4 pt-2">
      <div
        ref={chatContainerRef}
        className={cn(
          'relative flex h-full w-full flex-col rounded-16 bg-system-white08 light:bg-[rgba(255,255,255,0.54)]',
          isShowFile ? (storyDialogMessage ? 'w-2/3' : 'w-3/5') : 'w-full',
        )}
      >
        {isDraggingOver && (
          <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-16 border-2 border-[#508DFF] border-dashed bg-[rgba(80,141,255,0.06)]">
            <span className="text-[#508DFF] text-base">{t('create.dragUpload.placeholder')}</span>
          </div>
        )}
        <ChatHeader onShareClick={handleToggleShareSelection} shareDisabled={!activeSessionId || syncingShareIds} />
        <div className="flex min-h-0 w-full flex-1">
          <div className={cn('relative flex h-full min-h-0 min-w-0 flex-col', storyDialogMessage ? 'w-1/2' : 'w-full')}>
            <div ref={chatScrollRef} className="min-h-0 flex-1 overflow-y-auto" onScroll={handleChatScroll}>
              {(isHistoryBootstrapping || historyLoading || isAwaitingFirstMessageAfterCreate) &&
              messages.length === 0 ? (
                <ChatLoading />
              ) : messages.length === 0 ? (
                <ChatEmptySession />
              ) : (
                <div
                  className={cn(
                    'mx-auto flex w-full flex-col gap-4 px-8 py-4',
                    isShowFile ? 'max-w-[960px] px-6' : 'max-w-[1180px] px-8 lg:px-10',
                  )}
                >
                  <MessageList
                    ref={messageListRef}
                    messages={messages}
                    turnPhase={turnPhase}
                    clawId={activeClaw?.id}
                    sessionId={activeSessionId ?? undefined}
                    onPreviewFile={handlePreviewFile}
                    onQuoteFile={handleQuoteFile}
                    onOpenStoryDetail={handleOpenStoryDetail}
                    onSubmitStory={handleSubmitStory}
                    submittingStoryMessageIds={submittingStoryMessageIds}
                    scrollContainerRef={chatScrollRef}
                    onAssistantMeasureGrow={handleAssistantMeasureGrow}
                    onAssistantTypingEnd={handleAssistantTypingEnd}
                    shareSelection={{
                      enabled: isShareSelecting,
                      selectedMessageIds: selectedShareMessageIds,
                      onToggleMessage: handleToggleShareMessage,
                    }}
                  />
                </div>
              )}
            </div>
            {/* 一问一答两条消息以上再展示锚点列表，内部判断 user/assistant 锚点数大于 1 时才展示 */}
            {messages.length > 2 && (
              <AnchorCon anchors={anchors} scrollContainerRef={chatScrollRef} onJump={handleAnchorJump} />
            )}
            {showChatInput && (
              <div className="relative flex min-h-32 flex-col items-center gap-4 p-4">
                <div className={cn('relative w-full', isShowFile ? 'max-w-[960px]' : 'max-w-[1180px]')}>
                  {isReady && hasActiveTask && <TaskPanel />}
                  {isReady && phases.length > 0 && (
                    <SubtaskPanel
                      phases={phases}
                      expanded={expandedPanel === 'subtask'}
                      onExpandedChange={(expanded) => setExpandedPanel(expanded ? 'subtask' : null)}
                      scrollContainerRef={chatScrollRef}
                    />
                  )}
                  {isShareSelecting ? (
                    <div className="flex w-full items-center justify-between gap-3 rounded-16 border border-system-white08 bg-[#0A0D10] light:bg-[#F4F4FA] p-3">
                      <div className="flex items-center gap-2 light:text-system-text04 text-sm text-system-white64">
                        <Checkbox
                          checked={isAllShareMessagesSelected}
                          disabled={selectableShareMessageIds.length === 0}
                          className="text-base disabled:cursor-not-allowed disabled:opacity-30 data-[state=checked]:text-[#5765F2]"
                          onCheckedChange={handleToggleAllShareMessages}
                        />
                        <span>{t('claw.chat.share.selectAll')}</span>
                        <span className="h-3 w-px bg-system-white16 light:bg-system-text04/20" />
                        <span>{t('claw.chat.share.selectedCount', { count: selectedShareMessageCount })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="text"
                          className="h-9 rounded-8 px-4 light:text-system-text04 text-system-white64"
                          onClick={handleCancelShareSelection}
                        >
                          {t('common.dialog.cancel')}
                        </Button>
                        <Button
                          variant="solid"
                          className="h-9 rounded-8 px-4"
                          disabled={selectedShareMessageCount === 0 || sharingSession}
                          loading={sharingSession}
                          onClick={handleConfirmShareSelection}
                        >
                          {t('claw.nav.share')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <ChatInput
                      className={cn(isReady && hasActiveTask ? 'relative z-10 bg-[#0A0D10] light:bg-[#F4F4FA]' : '')}
                      ref={chatInputRef}
                      sending={chatInputSending}
                      onSend={chatInputOnSend}
                      onApplyForAccess={onApplyForAccess}
                      disableSend={disableChatInputSend}
                      disableUpload={disableChatInputUpload}
                      yoloActive={chatInputYoloActive}
                      onUploadSuccess={() => {
                        bumpClawFileListRefreshKey();
                      }}
                      onPreviewFile={handlePreviewFile}
                    />
                  )}
                </div>
                <ChatInputDisclaimer />
              </div>
            )}
          </div>
          {storyDialogMessage ? (
            <div className={cn('relative w-1/2 min-w-[360px]')}>
              <button
                type="button"
                aria-label="close story detail"
                onClick={() => setStoryDialogMessageId(null)}
                className="-translate-y-1/2 -translate-x-1/2 absolute top-1/2 left-0 z-10 flex h-12 w-5 items-center justify-center rounded-full bg-[#2C323D] text-system-white shadow transition-colors hover:bg-[#3E4451] hover:text-system-white80"
              >
                <ChevronRight className="size-3.5" />
              </button>
              <StoryDetailPanel
                // 不同 message 的 shot id 形如 `shot-1/2/3`（按 segment.index 生成），
                // 跨 message 会撞 id；StoryShotPane 内 editor 的 setInputValue effect
                // 只依赖 activeShot.id，撞 id 时不会刷新。用 message id 做 key 直接
                // remount 整块面板，draft / activeShotIndex / editor / 预览 url 缓存
                // 全部归零，确保从一个分镜消息切到另一个分镜消息时内容完整切换。
                key={storyDialogMessageId}
                storyPayload={storyDialogMessage.story ?? null}
                defaultFolderPath={
                  (activeSessionId ? sessionMediaFolderMap[activeSessionId] : '') || DEFAULT_MEDIA_ROOT
                }
                submitting={storyDialogMessageId ? submittingStoryMessageIds.has(storyDialogMessageId) : false}
                onSubmit={handleSubmitStoryFromDialog}
                onClose={() => setStoryDialogMessageId(null)}
              />
            </div>
          ) : null}
        </div>
      </div>
      {isShowFile ? (
        <div className={cn('min-w-[320px]', storyDialogMessage ? 'w-1/3' : 'w-2/5 min-w-[360px]')}>
          <MediaBrowser
            previewTargetFilePath={pendingPreviewFilePath}
            onPreviewHandled={() => setPendingPreviewFilePath(null)}
            syncing={restoringSessionMedia}
            onInsertAsset={(asset) => {
              chatInputRef.current?.insertAssetMention(asset);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
